import { Button } from "@shltelemedicine/shl-ui-lib";
import fileDownload from "js-file-download";
import moment from "moment";
import { useCallback, useState } from "react";
import { Spinner } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import nextIcon from "../../assets/buttons/next.svg";
import { Constants } from "../../constants";
import useAlert from "../../hooks/useAlert";
import useAuthorizedAction, {
  AuthorizedActionsKeys,
} from "../../hooks/useAuthorizedAction";
import { ReportTypes } from "../../models/reports.interface";
import reportsService from "../../services/reports.service";
import { generateFileName, isWithContent } from "../../utils/fileUtil";
import AlertModal from "../Shared/AlertModal/AlertModal";
import CheckBoxFormInput from "../Shared/CheckBox/CheckBoxFormInput";
import DateFormInput from "../Shared/DateInput/DateFormInput";
import EntitySelectFormInput from "../Shared/EntitySelectFormInput/EntitySelectFormInput";
import styles from "./Reports.module.css";
import ReportTypesSelectFormInput from "./ReportTypesSelectFormInput/ReportTypesSelectFormInput";
import StudySelectFormInput from "./StudySelectFormInput/StudySelectFormInput";

type ReportsFormInputs = {
  type: string;
  fromDate: string;
  toDate: string;
  entity: string;
  study: string;
  checkActive: boolean;
};

const datesInit = {
  fromDate: moment()
    .startOf("month")
    .format("YYYY-MM-DD"),
  toDate: moment()
    .endOf("month")
    .format("YYYY-MM-DD"),
};

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [customDate, setCustomDate] = useState(false);
  const [entity, setEntity] = useState("");

  const {
    showErrorAlert,
    showInfoAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();

  const isCroOrg = useAuthorizedAction(AuthorizedActionsKeys.ViewAsCroOrg);
  const isCroExternOrg = useAuthorizedAction(
    AuthorizedActionsKeys.ViewAsCroExternOrg
  );
  const isProfessional = useAuthorizedAction(
    AuthorizedActionsKeys.ViewAsProfessional
  );

  const {
    watch,
    handleSubmit,
    control,
    setValue,
    formState: { errors: formErrors },
  } = useForm<ReportsFormInputs>({
    defaultValues: {
      fromDate: moment()
        .subtract(1, "months")
        .startOf("month")
        .format("YYYY-MM-DD"),
      toDate: moment()
        .subtract(1, "months")
        .endOf("month")
        .format("YYYY-MM-DD"),
      checkActive: false,
    },
  });

  const fromDate = watch("fromDate");
  const toDate = watch("toDate");
  const isTypeNonPro = watch("type") === ReportTypes.NonProUsers;
  const isTypePro = watch("type") === ReportTypes.ProUsers;

  const showStudySelection = isCroOrg || isCroExternOrg;
  const showEntitySelection =
    !(isCroOrg || isCroExternOrg) && !isProfessional && !isTypePro;

  const submitHandler = async (reportsForm: ReportsFormInputs) => {
    setLoading(true);
    const { type, entity, study, fromDate, toDate, checkActive } = reportsForm;

    try {
      const {
        success,
        data: reportFile,
        errorCode,
      } = await reportsService.getReport(
        type,
        fromDate,
        toDate,
        entity,
        study,
        checkActive
      );

      if (!success || !reportFile) {
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.DOWNLOADING_REPORT",
          "An error occurred while downloading the report.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }

      const reportText = await reportFile.text();

      if (!reportFile.size) {
        const { message, support } = getAlertMessageAndSupportContent(
          "REPORTS.EMPTY_REPORT",
          "The report is empty.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showInfoAlert(message, support);
        return;
      }

      if (!isWithContent(reportText, reportFile)) {
        const { message, support } = getAlertMessageAndSupportContent(
          "REPORTS.EMPTY_DATA",
          "There is no data for this date range.<br></br>Please verify the date range and try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showInfoAlert(message, support);
        return;
      }

      const fileName = generateFileName({
        prefix: getPrefixFromReportType(type),
        content: getFileNameContent(type),
        fileExtension: "csv",
      });

      fileDownload(reportFile, fileName);
    } finally {
      setLoading(false);
    }
  };

  const getPrefixFromReportType = (type: string) => {
    switch (type) {
      case ReportTypes.ProUsers:
        return "Smartheart_Professionals";
      case ReportTypes.NonProUsers:
        return "Smartheart_Patients";
      case ReportTypes.EcgList:
        return "Smartheart_ECG";
      default:
        return "Smartheart_Report";
    }
  };

  const getFileNameContent = (type: string) => [
    showStudySelection
      ? "Study"
      : showEntitySelection && type !== ReportTypes.NonProUsers
      ? entity
      : "",
    moment().format(Constants.DATE_FORMAT),
  ];

  const nextHandler = () => {
    setValue(
      "fromDate",
      moment(fromDate)
        .add(1, "months")
        .startOf("month")
        .format("YYYY-MM-DD")
    );
    setValue(
      "toDate",
      moment(toDate)
        .add(1, "months")
        .endOf("month")
        .format("YYYY-MM-DD")
    );
  };

  const backHandler = () => {
    setValue(
      "fromDate",
      moment(fromDate)
        .subtract(1, "months")
        .startOf("month")
        .format("YYYY-MM-DD")
    );
    setValue(
      "toDate",
      moment(toDate)
        .subtract(1, "months")
        .endOf("month")
        .format("YYYY-MM-DD")
    );
  };

  const customDateToggleHandler = () => {
    setCustomDate((prev) => !prev);

    setValue("fromDate", datesInit.fromDate);
    setValue("toDate", datesInit.toDate);
  };

  const setDefaultReportType = useCallback(
    (value: string): void => {
      setValue("type", value);
    },
    [setValue]
  );

  const disableNext =
    toDate ===
    moment()
      .endOf("month")
      .format("YYYY-MM-DD");

  return (
    <>
      <Form className="pb-5">
        <Form.Row className="flex-column align-items-inherit align-items-lg-end pb-5 flex-lg-row">
          <Col xs="auto">
            <Form.Label className="mb-2 app-form-label">
              <FormattedMessage id="REPORTS.TYPE" defaultMessage="Type" />
            </Form.Label>
            <ReportTypesSelectFormInput
              required
              isInvalid={!!formErrors?.type}
              control={control}
              setDefaultReportType={setDefaultReportType}
            />
          </Col>
          {!isTypePro && !isTypeNonPro && (
            <Col xs="auto">
              <Form.Label className="d-flex align-items-center mt-1 mt-xs-0 mb-2 app-form-label">
                <span>
                  <FormattedMessage id="REPORTS.DATES" defaultMessage="Dates" />
                </span>
                <Button
                  variant=""
                  className="p-0 px-2 text-decoration-link capitalized"
                  onClick={customDateToggleHandler}
                >
                  <span className={customDate ? "prim-color" : ""}>
                    <FormattedMessage
                      id="REPORTS.CUSTOM_DURATION"
                      defaultMessage="Custom Duration"
                    />
                  </span>
                </Button>
              </Form.Label>
              <div className="d-flex align-items-center">
                <Button
                  title="Previous month"
                  className={`${styles.directionButton} ${styles.rotate}`}
                  onClick={backHandler}
                  disabled={customDate}
                >
                  <img src={nextIcon} alt="" />
                </Button>
                <div className="mx-sm"></div>
                <DateFormInput
                  name={"fromDate"}
                  control={control}
                  rules={{
                    required: true,
                  }}
                  input={{
                    readOnly: !customDate,
                    isInvalid: !!formErrors?.fromDate,
                    isClearable: false,
                    dateFormat: "MMM d yyyy",
                    max: toDate ? moment(toDate).toDate() : new Date(),
                  }}
                />
                <div className="mx-sm"></div>
                <DateFormInput
                  name={"toDate"}
                  control={control}
                  rules={{
                    required: true,
                  }}
                  input={{
                    readOnly: !customDate,
                    isInvalid: !!formErrors?.fromDate,
                    isClearable: false,
                    dateFormat: "MMM d yyyy",
                    min: fromDate ? moment(fromDate).toDate() : undefined,
                    max: new Date(),
                  }}
                />
                <div className="mx-sm"></div>
                <Button
                  title="Next month"
                  className={`${styles.directionButton} `}
                  onClick={nextHandler}
                  disabled={customDate || disableNext}
                >
                  <img src={nextIcon} alt="" />
                </Button>
              </div>
            </Col>
          )}
          <Col xs="auto">
            {showStudySelection && (
              <>
                <Form.Label className="my-2 app-form-label">
                  <FormattedMessage id="STUDIES.STUDY" defaultMessage="Study" />
                </Form.Label>
                <div className={styles.dropDown}>
                  <StudySelectFormInput
                    required
                    isInvalid={!!formErrors?.study}
                    control={control}
                  />
                </div>
              </>
            )}
            {showEntitySelection && (
              <>
                <Form.Label className="my-2 app-form-label">
                  <FormattedMessage
                    id="MANAGEMENT.ENTITY"
                    defaultMessage="Entity"
                  />
                </Form.Label>
                <div className={styles.dropDown}>
                  <EntitySelectFormInput
                    required
                    isInvalid={!!formErrors?.entity}
                    control={control}
                    restricted={isTypeNonPro ? "non-pro" : undefined}
                    setValueOnChange={(value) => setEntity(value)}
                  />
                </div>
              </>
            )}
          </Col>
          <Col xs="auto">
            {isTypeNonPro ? (
              <CheckBoxFormInput
                name={"checkActive"}
                control={control}
                input={{
                  className: `${styles.checkActive} justify-content-start justify-content-lg-center`,
                  label: (
                    <FormattedMessage
                      id="ACTIVE"
                      defaultMessage="Active only"
                    />
                  ),
                }}
              />
            ) : null}
          </Col>
          <Col xs="auto" className="d-flex justify-content-end">
            <Button
              bgColor="transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
              className="align-self-end capitalized col-auto mt-3 mt-lg-0"
              disabled={loading}
              onClick={handleSubmit(submitHandler)}
            >
              {loading && (
                <>
                  <Spinner as="span" animation="border" size="sm" />
                  &nbsp;&nbsp;
                </>
              )}
              {!loading ? (
                <FormattedMessage
                  id="REPORTS.EXPORT_CSV"
                  defaultMessage="Export to CSV"
                />
              ) : (
                <>
                  <FormattedMessage
                    id="GLOBAL.DOWNLOADING"
                    defaultMessage="Downloading"
                  />
                  ...
                </>
              )}
            </Button>
          </Col>
        </Form.Row>
        <AlertModal {...alertProps}>{alertMessage}</AlertModal>
      </Form>
    </>
  );
};

export default Reports;
